"use client";

import { FoodCreatorLoadingScreen } from "@/components/food-creator/FoodCreatorAuthGuard";
import { CribNoshLogo } from "@/components/ui/CribNoshLogo";
import { CustomFoodCreatorDashboardIcon } from "@/components/ui/CustomFoodCreatorDashboardIcon";
import { CustomFoodCreatorOrdersIcon } from "@/components/ui/CustomFoodCreatorOrdersIcon";
import { CustomFoodCreatorProfileIcon } from "@/components/ui/CustomFoodCreatorProfileIcon";
import { useFoodCreatorAuth } from "@/lib/food-creator-auth";
import { ArrowRight, Sparkles } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useEffect } from "react";

const desktopPillars = [
  {
    title: "Orders",
    description: "Review what is already in motion.",
    icon: CustomFoodCreatorOrdersIcon,
  },
  {
    title: "Menu",
    description: "Update meals, drinks, and pricing.",
    icon: CustomFoodCreatorDashboardIcon,
  },
  {
    title: "Setup",
    description: "Keep payouts, profile, and compliance current.",
    icon: CustomFoodCreatorProfileIcon,
  },
];

export default function FoodCreatorPortalEntry() {
  const router = useRouter();
  const { isAuthenticated, isLoading } = useFoodCreatorAuth();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      router.replace("/food-creator/dashboard");
    }
  }, [isAuthenticated, isLoading, router]);

  if (isLoading) {
    return <FoodCreatorLoadingScreen label="Loading your creator portal..." />;
  }

  if (isAuthenticated) {
    return <FoodCreatorLoadingScreen label="Opening your dashboard..." />;
  }

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#FAFCF8] text-[#031D11]">
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -left-24 top-0 h-72 w-72 rounded-full bg-[#FFE2D7] blur-[110px]" />
        <div className="absolute right-[-3rem] top-0 h-72 w-72 rounded-full bg-[#E8F7EC] blur-[110px]" />
      </div>

      <section className="relative mx-auto flex min-h-screen w-full max-w-6xl flex-col px-4 pb-12 pt-6 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-4">
          <CribNoshLogo size={128} variant="default" />
          <Link
            href="/food-creator/sign-in?returnTo=%2Ffood-creator%2Fdashboard"
            className="inline-flex h-11 items-center justify-center rounded-full bg-[#094327] px-5 text-sm font-semibold text-white transition hover:bg-[#0B5630]"
          >
            Sign in
          </Link>
        </div>

        <div className="mt-8 overflow-hidden rounded-[30px] bg-[#FFF8F1]">
          <div className="relative px-6 pb-8 pt-7 sm:px-8 sm:pb-10">
            <Image
              src="/food-creator/mobile-parity/illustrations/creator-dashboard-wallpaper-v2.png"
              alt=""
              fill
              className="object-cover object-center opacity-30"
            />
            <Image
              src="/food-creator/mobile-parity/illustrations/dashboard-header-cloud-v1.png"
              alt=""
              width={1600}
              height={320}
              className="pointer-events-none absolute left-1/2 top-0 w-[130%] max-w-none -translate-x-1/2 opacity-95"
            />

            <div className="relative z-10 space-y-7">
              <div className="inline-flex items-center gap-3 rounded-full bg-white/78 px-3 py-2">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white">
                  <CustomFoodCreatorDashboardIcon size={22} color="#094327" />
                </div>
                <span className="text-sm font-semibold text-[#094327]">Food Creator portal</span>
              </div>

              <div className="max-w-3xl space-y-3">
                <h1 className="text-4xl leading-none tracking-tight text-[#031D11] sm:text-5xl">
                  Food creator tools for the web
                </h1>
                <p className="max-w-2xl text-sm leading-6 text-[#5C6B61] sm:text-base">
                  Handle orders, menu updates, payouts, and setup here. Capture-first tools stay in the app.
                </p>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                {desktopPillars.map((pillar) => (
                  <div key={pillar.title} className="px-1 py-2">
                    <div className="flex h-11 w-11 items-center justify-center text-[#094327]">
                      <pillar.icon size={22} color="#094327" />
                    </div>
                    <p className="mt-4 text-lg text-[#031D11]">{pillar.title}</p>
                    <p className="mt-1 text-sm text-[#6B7280]">{pillar.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(280px,0.72fr)]">
          <div className="px-1 py-1 sm:px-2">
            <div className="flex items-start gap-3">
              <div className="p-1">
                <Sparkles className="h-5 w-5 text-[#094327]" />
              </div>
              <div className="space-y-2">
                <p className="text-sm font-semibold text-[#031D11]">What web is for</p>
                <p className="text-sm leading-6 text-[#6B7280]">
                  Use web when you need speed, review, and account control. Use the app when the task depends on capture, live tools, or creation on the move.
                </p>
              </div>
            </div>
          </div>

          <div className="space-y-3 px-1 py-1 sm:px-2">
            <Link
              href="/food-creator/sign-in?returnTo=%2Ffood-creator%2Fdashboard"
              className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-full bg-[#FF3B30] px-5 text-sm font-semibold text-white transition hover:bg-[#ff554a]"
            >
              Continue to sign in
              <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              href="/become-a-food-creator/apply?type=food-creator"
              className="inline-flex h-12 w-full items-center justify-center rounded-full bg-[#F4F8F2] px-5 text-sm font-semibold text-[#094327] transition hover:bg-[#EAF4ED]"
            >
              Apply to cook
            </Link>
            <Link
              href="/food-creator/sign-in?returnTo=%2Ffood-creator%2Fdashboard"
              className="inline-flex h-12 w-full items-center justify-center rounded-full border border-[#D7E8D7] px-5 text-sm font-semibold text-[#031D11] transition hover:bg-[#FAFCF8]"
            >
              Open creator dashboard
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
